import { useState, useEffect, Dispatch, SetStateAction } from 'react'
import { AssetReference } from '@opallabs/cassini'
import { getNestedProperty } from '@opallabs/lambda'
import {
  FlexDirections,
  FirstAssetRatios,
  GalleryPosition,
  GalleryPositionGroups,
} from '../positions'

const EMPTY_GALLERY_POSITIONS = [
  { direction: FlexDirections.Row, height: 0, width: 0, numAssets: 0 },
]

export type UseGalleryPositionsReturn = {
  orderedAssets: AssetReference[]
  setOrderedAssets: Dispatch<SetStateAction<AssetReference[]>>
  galleryPositions: GalleryPosition[]
}

export default function useGalleryPositions(
  assets: AssetReference[],
  maxAssets: number,
  positions: GalleryPositionGroups[][]
): UseGalleryPositionsReturn {
  const [galleryPositions, setGalleryPositions] = useState<GalleryPosition[]>(
    EMPTY_GALLERY_POSITIONS
  )
  const [orderedAssets, setOrderedAssets] = useState<AssetReference[]>(assets)

  useEffect(() => {
    setOrderedAssets(assets)

    if (!assets || assets.length === 0) return

    const firstAssetHeight: number = getNestedProperty(assets[0], 'height') || 0
    const firstAssetWidth: number = getNestedProperty(assets[0], 'width') || 0
    const imageOrientations = {
      firstAsset: FirstAssetRatios.Landscape,
      portrait: 0,
      landscape: 0,
      square: 0,
    }

    if (firstAssetHeight > firstAssetWidth)
      imageOrientations.firstAsset = FirstAssetRatios.Portrait
    else if (firstAssetHeight === firstAssetWidth)
      imageOrientations.firstAsset = FirstAssetRatios.Square

    const visibleAssets = assets.slice(0, maxAssets)
    visibleAssets.forEach((asset) => {
      const assetHeight: number = getNestedProperty(asset, 'height') || 0
      const assetWidth: number = getNestedProperty(asset, 'width') || 0
      if (assetHeight > assetWidth) {
        imageOrientations.portrait += 1
      } else if (assetHeight === assetWidth) {
        imageOrientations.square += 1
      } else {
        imageOrientations.landscape += 1
      }
    })

    // get possible positions grouping based on number of assets
    const positionsForAssets =
      assets.length > positions.length
        ? positions[positions.length - 1]
        : positions[assets.length - 1]

    // special check for facebook standard
    const facebookFirstAsset = positionsForAssets[0].validations.firstAsset
    if (
      facebookFirstAsset &&
      facebookFirstAsset === FirstAssetRatios.Disregard
    ) {
      if (imageOrientations.firstAsset === FirstAssetRatios.Portrait)
        imageOrientations.portrait -= 1
      else if (imageOrientations.firstAsset === FirstAssetRatios.Square)
        imageOrientations.square -= 1
      else imageOrientations.landscape -= 1
    }

    // find position grouping based on validations check
    const matchedPositions = positionsForAssets.find((positions) => {
      if (Object.keys(positions.validations).length === 0) return true
      let validationsPassed = false
      switch (true) {
        case positions.validations.firstAsset &&
          positions.validations.firstAsset === FirstAssetRatios.Disregard:
          validationsPassed = true
          break
        case positions.validations.firstAsset &&
          positions.validations.firstAsset === imageOrientations.firstAsset:
          validationsPassed = true
          break
        case positions.validations.landscape &&
          positions.validations.landscape <= imageOrientations.landscape:
          validationsPassed = true
          break
        case positions.validations.square &&
          positions.validations.square <= imageOrientations.square:
          validationsPassed = true
          break
        case positions.validations.portrait &&
          positions.validations.portrait <= imageOrientations.portrait:
          validationsPassed = true
          break
        default:
          validationsPassed = false
          break
      }
      return validationsPassed
    })

    setGalleryPositions(matchedPositions?.groups || EMPTY_GALLERY_POSITIONS)
  }, [assets, positions, maxAssets])

  return { orderedAssets, setOrderedAssets, galleryPositions }
}
